import { MyAgentsResponse } from '@tabel/shared/schemas/me-agents';
import { ApiError } from '../../lib/errors';
import { contextHeaders } from '../../lib/role';

/*
 * Адреса страницы «Мои ИИ-клиенты» (005, T059, T147; specs/005-rules-mcp/contracts/http-api.md): `GET /api/me/agents` —
 * мои подключения, `DELETE /api/me/agents/:grantId` — отозвать (`revokeAgentGrants`). Оба — под входом сессии 004 с
 * заголовками роли и объекта, только свои подключения; чужое и уже отозванное — `404`. Ошибки — тело 004
 * `{ error: { code, message, requestId, details? } }`.
 *
 * Ответ — общая схема `MyAgentsResponse` (`@tabel/shared/schemas/me-agents`), та же, что у сервера (T058). Права — пара
 * «код — текст», как на странице согласия: тексты прав приходят с сервера. Ответ — объект, а не массив: в 006 рядом с
 * `agents` встанут компьютеры, и страница получит их из той же схемы.
 */

export type { MyAgent, MyAgentsResponse } from '@tabel/shared/schemas/me-agents';
export type MyAgentState = MyAgentsResponse['agents'][number]['state'];

const isObject = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null;
const isText = (v: unknown): v is string => typeof v === 'string';

/** Ответ не по схеме (например, HTML вместо JSON) — сбой сервера с кодом ошибки. */
const badAnswer = (status: number, requestId: string | null) =>
  new ApiError(status, 'INTERNAL', 'Ошибка сервера', requestId);

async function call(method: 'GET' | 'DELETE', url: string): Promise<{ data: unknown; res: Response }> {
  let res: Response;
  try {
    // Тела нет: без `Content-Type`. `Origin` браузер ставит сам, сервер его проверяет (004).
    res = await fetch(url, { method, credentials: 'include', headers: contextHeaders() });
  } catch {
    throw new ApiError(0, 'NETWORK', 'Нет связи с сервером', null);
  }
  const requestId = res.headers.get('x-request-id');
  if (res.status === 204) return { data: undefined, res };
  const data: unknown = await res.json().catch(() => undefined);
  if (!res.ok) {
    const e = isObject(data) && isObject(data.error) ? data.error : {};
    const code = isText(e.code)
      ? (e.code as ApiError['code'])
      : res.status === 404
        ? 'NOT_FOUND'
        : 'INTERNAL';
    // Сессия кончилась — страница входа, как у остальных страниц (`lib/api.ts`).
    if (res.status === 401 && code === 'UNAUTHENTICATED') location.assign('/login');
    throw new ApiError(
      res.status,
      code,
      isText(e.message) ? e.message : `Ошибка ${res.status}`,
      isText(e.requestId) ? e.requestId : requestId,
      undefined,
      isObject(e.details) ? e.details : undefined,
    );
  }
  return { data, res };
}

/**
 * Мои подключения. Ответ — по схеме `MyAgentsResponse`: лишние поля дальше не идут; не по схеме — ошибка с кодом, а не
 * пустой список (иначе страница показала бы «Подключений нет» или пустые строки).
 */
export async function myAgents(): Promise<MyAgentsResponse> {
  const { data, res } = await call('GET', '/api/me/agents');
  const parsed = MyAgentsResponse.safeParse(data);
  if (!parsed.success) throw badAnswer(res.status, res.headers.get('x-request-id'));
  return parsed.data;
}

/** Отозвать подключение. Ответ — `204` или JSON: тело не нужно. */
export async function revokeAgent(grantId: string): Promise<void> {
  await call('DELETE', `/api/me/agents/${encodeURIComponent(grantId)}`);
}

/** Подключения уже нет: отозвано раньше или чужое (`404`). */
export const isGone = (err: unknown): boolean => err instanceof ApiError && err.status === 404;

/*
 * «Мои выборки» (005, T140; FR-026; contracts/http-api.md, `GET /api/me/samples`) — запрос и ключ кэша общие с
 * «Передать день агенту» (`components/DayCardAgentLink.tsx`: `requestMySamples`, `mySamplesKey`), ответ — схема
 * `MySamplesResponse`. Роли раздела — строка «Выборки дней» таблицы прав (`AGENT_LINK_ROLES`, правило `me.samples`):
 * администратор правил, утверждающий, проверяющий. Администратор системы выборок не делает — раздела у него нет.
 */
export { AGENT_LINK_ROLES as SAMPLE_ROLES } from '../../components/DayCardAgentLink';
